import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Spinner,
  Alert,
  Pagination,
  Badge,
  Modal,
  Form,
  InputGroup,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import ReviewModal from "../components/ReviewModal";
import Swal from "sweetalert2";

export default function ManageBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentBookingForReview, setCurrentBookingForReview] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [currentBill, setCurrentBill] = useState(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState(null);

  // Search state for category modal
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchBookings = async (pageNo = 1) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/customer/bookings`, {
        params: { page: pageNo, pageSize },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(res.data.bookings || []);
      setTotalPages(res.data.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      console.error("Fetch bookings error:", err);
      setError("Failed to load bookings: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBill = async (bookingId) => {
    setBillLoading(true);
    setBillError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_BASE_URL}/customer/bookings/${bookingId}/invoice`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCurrentBill(res.data.invoice || null);
      setShowBillModal(true);
    } catch (err) {
      console.error("Fetch bill error:", err);
      setBillError("Failed to load bill: " + err.message);
      Swal.fire("Error", "Failed to load bill: " + err.message, "error");
    } finally {
      setBillLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const response = await axios.get(`${API_BASE_URL}/api/categories`, {
        headers: { "Content-Type": "application/json" },
      });
      const categoriesData = response.data;

      const categoriesWithSubcategories = await Promise.all(
        categoriesData.map(async (category) => {
          try {
            const subcategoriesResponse = await axios.get(
              `${API_BASE_URL}/api/categories/${category.id}/subcategories`,
              {
                headers: { "Content-Type": "application/json" },
              }
            );
            return {
              ...category,
              icon: getCategoryIcon(category.name),
              subcategories: subcategoriesResponse.data || [],
            };
          } catch (err) {
            console.error(
              `Failed to fetch subcategories for category ${category.name}:`,
              err
            );
            return {
              ...category,
              icon: getCategoryIcon(category.name),
              subcategories: [],
            };
          }
        })
      );

      setCategories(categoriesWithSubcategories);
      setFilteredCategories(categoriesWithSubcategories);
      setCategoriesLoading(false);
    } catch (err) {
      console.error("Fetch categories error:", err);
      setCategoriesError(
        `Error fetching categories: ${err.message}. Please try again later.`
      );
      setCategoriesLoading(false);
    }
  };

  // Debounced search function
  const handleSearch = useCallback(
    (searchValue) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      setSearchTimeout(
        setTimeout(() => {
          if (!searchValue.trim()) {
            setFilteredCategories(categories);
            return;
          }

          const lowerSearch = searchValue.toLowerCase().trim();
          const filtered = categories
            .map((category) => {
              const matchingSubcategories = category.subcategories.filter(
                (subcat) => subcat.name.toLowerCase().includes(lowerSearch)
              );

              if (
                category.name.toLowerCase().includes(lowerSearch) ||
                matchingSubcategories.length > 0
              ) {
                return {
                  ...category,
                  subcategories: matchingSubcategories,
                };
              }
              return null;
            })
            .filter(Boolean);

          setFilteredCategories(filtered);
        }, 300)
      );
    },
    [categories, searchTimeout]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredCategories(categories);
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      "Professional & Business Services": "bi-briefcase-fill",
      "Beauty, Grooming & Wellness": "bi-scissors",
      "Healthcare & Medical": "bi-heart-pulse",
      "Home & Repair Services": "bi-tools",
      "Real Estate & Property": "bi-house-fill",
      "Automobile & Transport": "bi-car-front-fill",
      "Events & Lifestyle": "bi-camera-fill",
      "Education & Training": "bi-book-fill",
      "Pet & Animal Care": "bi-paw",
      "Travel & Leisure": "bi-airplane-fill",
      "Food & Beverages": "bi-cup-straw",
      "Laundry & Maintenance": "bi-bucket-fill",
      "Childcare & Elderly Services": "bi-person-heart",
      "Miscellaneous / Local Utility": "bi-gear-fill",
    };
    return iconMap[categoryName] || "bi-gear-fill";
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?"))
      return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/customer/bookings/${bookingId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Swal.fire("Success", "Booking cancelled successfully", "success");
      fetchBookings(page);
    } catch (err) {
      console.error("Cancel booking error:", err);
      Swal.fire("Error", "Failed to cancel booking: " + err.message, "error");
    }
  };

  const openReview = (booking) => {
    setCurrentBookingForReview(booking);
    setShowReviewModal(true);
  };

  const onReviewSaved = () => {
    fetchBookings(page);
  };

  const handleNewBookingClick = () => {
    setShowCategoryModal(true);
    fetchCategories();
  };

  useEffect(() => {
    fetchBookings(1);
  }, []);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleString();
  };

  const formatCurrency = (cents) => {
    if (cents == null) return "-";
    return `₹${cents.toFixed(2)}`;
  };

  const renderStatusBadge = (status) => {
    const st = status?.toLowerCase();
    if (st === "confirmed") return <Badge bg="success">Confirmed</Badge>;
    if (st === "cancelled") return <Badge bg="danger">Cancelled</Badge>;
    return (
      <Badge bg="warning" text="dark">
        Pending
      </Badge>
    );
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My Appointments</h2>
        <Button variant="success" onClick={handleNewBookingClick}>
          <i className="bi bi-plus-circle me-2"></i>New Booking
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <Spinner animation="border" />
      ) : bookings.length === 0 ? (
        <p>No appointments found.</p>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Service</th>
                <th>Company</th>
                <th>Branch</th>
                <th>Scheduled At</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => {
                const serviceList =
                  (b.items || []).map((it) => it.service_name).join(", ") ||
                  "-";
                const firstItem =
                  b.items && b.items.length > 0 ? b.items[0] : null;
                const scheduledAt = firstItem
                  ? firstItem.scheduled_start
                  : null;
                const totalAmount =
                  b.total_amount_cents || (firstItem?.price_cents ?? 0);
                const isFuture =
                  scheduledAt && new Date(scheduledAt) > new Date();
                const isCancelled = b.status?.toLowerCase() === "cancelled";
                const isPaid = b.payment_status?.toLowerCase() === "paid";

                return (
                  <tr key={b.id}>
                    <td>
                      <Link
                        to={`/booking-success/${b.id}`}
                        style={{ textDecoration: "none", color: "#007bff" }}
                        title="View booking details"
                      >
                        <strong>{(page - 1) * pageSize + i + 1}</strong>
                      </Link>
                    </td>
                    <td>{serviceList}</td>
                    <td>{b.company_name}</td>
                    <td>{b.branch_name || "All"}</td>
                    <td>{formatDate(scheduledAt)}</td>
                    <td>{renderStatusBadge(b.status)}</td>
                    <td>{formatCurrency(totalAmount)}</td>
                    <td className="d-flex align-items-center gap-2">
                      {!isCancelled && isFuture ? (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleCancel(b.id)}
                        >
                          Cancel
                        </Button>
                      ) : null}
                      {b.status?.toLowerCase() === "confirmed" ? (
                        b.review ? (
                          <div
                            title={b.review.feedback || "No feedback provided"}
                            className="d-inline-block"
                          >
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i
                                key={i}
                                className={`bi ${
                                  i < b.review.rating
                                    ? "bi-star-fill text-warning"
                                    : "bi-star text-muted"
                                } me-1`}
                              />
                            ))}
                          </div>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openReview(b)}
                          >
                            <i className="bi bi-emoji-smile"></i> Review
                          </Button>
                        )
                      ) : null}
                      {isPaid ? (
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => fetchBill(b.id)}
                          disabled={billLoading}
                          title="View Bill"
                        >
                          <i className="bi bi-receipt"></i>
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              {[...Array(totalPages)].map((_, i) => (
                <Pagination.Item
                  key={i}
                  active={page === i + 1}
                  onClick={() => fetchBookings(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        </>
      )}

      <ReviewModal
        show={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        booking={currentBookingForReview}
        onSaved={onReviewSaved}
      />

      {/* Bill Modal */}
      <Modal
        show={showBillModal}
        onHide={() => setShowBillModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-receipt me-2 text-info"></i>
            Invoice Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {billLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" className="me-2" />
              Loading invoice...
            </div>
          ) : billError ? (
            <Alert variant="danger">{billError}</Alert>
          ) : !currentBill ? (
            <p>No invoice details available.</p>
          ) : (
            <div>
              <h5>Invoice #{currentBill.id}</h5>
              <Table striped bordered hover size="sm">
                <tbody>
                  <tr>
                    <th>Customer</th>
                    <td>{currentBill.customer_name || "-"}</td>
                  </tr>
                  <tr>
                    <th>Branch</th>
                    <td>{currentBill.branch_name || "All"}</td>
                  </tr>
                  <tr>
                    <th>Services</th>
                    <td>
                      {currentBill.service_names &&
                      currentBill.service_names.length > 0
                        ? currentBill.service_names.join(", ")
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Total Amount</th>
                    <td>₹{(currentBill.total_amount_cents || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <th>Tax</th>
                    <td>₹{(currentBill.tax_cents || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <th>Discount</th>
                    <td>₹{(currentBill.discount_cents || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <Badge
                        bg={
                          currentBill.status === "paid" ? "success" : "warning"
                        }
                      >
                        {currentBill.status}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Created At</th>
                    <td>{formatDate(currentBill.created_at)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBillModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Category Selection Modal */}
      <Modal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-search me-2 text-success"></i>
            Select a Service Category
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-4 border-bottom bg-light">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search categories or subcategories..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="rounded-end"
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-circle"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <small className="text-muted mt-1 d-block">
                Found {filteredCategories.length}{" "}
                {filteredCategories.length === 1 ? "match" : "matches"}
              </small>
            )}
          </div>
          <div className="p-4" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {categoriesLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" className="me-2" />
                Loading categories...
              </div>
            ) : categoriesError ? (
              <div className="text-center py-5">
                <i
                  className="bi bi-exclamation-triangle text-danger mb-2"
                  style={{ fontSize: "2rem" }}
                ></i>
                <div className="text-danger">{categoriesError}</div>
              </div>
            ) : filteredCategories.length === 0 ? (
              searchTerm ? (
                <div className="text-center py-5">
                  <i
                    className="bi bi-search-x text-muted mb-3"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <h5 className="text-muted">No results found</h5>
                  <p className="text-muted">
                    Try searching with different keywords
                  </p>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i
                    className="bi bi-inbox text-muted mb-3"
                    style={{ fontSize: "3rem" }}
                  ></i>
                  <h5>No categories available</h5>
                </div>
              )
            ) : (
              <div className="row g-3">
                {filteredCategories.map((category) => (
                  <div className="col-md-6 col-lg-4" key={category.id}>
                    <div className="card h-100 shadow-sm hover-card">
                      <div className="card-body">
                        <h6 className="card-title fw600 mb-3 d-flex align-items-center">
                          <i
                            className={`bi ${category.icon} me-2 text-success fs-5`}
                          ></i>
                          {category.name}
                        </h6>
                        {category.subcategories.length > 0 ? (
                          <ul className="list-unstyled mb-0">
                            {category.subcategories.map((subcat) => (
                              <li key={subcat.id} className="mb-2">
                                <Link
                                  to={`/explore?categoryId=${
                                    category.id
                                  }&categoryName=${encodeURIComponent(
                                    category.name
                                  )}&subcategoryId=${
                                    subcat.id
                                  }&subcategoryName=${encodeURIComponent(
                                    subcat.name
                                  )}`}
                                  className="text-decoration-none text-dark subcategory-link"
                                  onClick={() => setShowCategoryModal(false)}
                                >
                                  <i className="bi bi-chevron-right me-2 text-success"></i>
                                  {subcat.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted small mb-0">
                            No subcategories available
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => setShowCategoryModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .hover-card:hover {
          transform: translateY(-2px);
          transition: all 0.2s ease-in-out;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
        .subcategory-link:hover {
          color: #28a745 !important;
        }
        .subcategory-link:hover i {
          transform: translateX(2px);
          transition: transform 0.2s ease-in-out;
        }
      `}</style>
    </div>
  );
}
